/** Tests uses the same direct study list and current-state projector as the builder. */
import { useStudyAuthoringList } from '../hooks/study-builder/useStudyAuthoringList'
import { productLanguage } from '@testfeed/product-language'
import type { ShopifyAppBridge } from '../shopifyApiFetch'
import { buildSettingsViewUrl } from '../resultsNavigation'
import type { VerifiedTestFeedConnectionScope } from '../ShopifyAppContext'
import { introForView, pageTitleForView } from '../shopifyPageCopy'
import type { TestFeedConnectionStatus } from '../typesStore'
import { RunningTestsSection } from './RunningTestsSection'
import { StudySetupsPage, type StudySetupsConnectionState } from './StudySetupsPage'
import { buildTestsIndexRecords } from './tests-index/testsIndexRecords'
import { trackShopifyAction, trackShopifyRefetch } from '../shopifyUiTracking'
import { useBulkStudyDeletion } from '../hooks/study-builder/useBulkStudyDeletion'
import { BulkStudyDeleteDialog } from './StudySetupDeleteDialog'

export interface StudySetupsRouteProps {
  shopify: ShopifyAppBridge
  verifiedScope: VerifiedTestFeedConnectionScope | null
  isTestFeedConnected: boolean
  readiness: TestFeedConnectionStatus | null
  isLoadingReadiness: boolean
  onCreateTest: () => void
  onResumeDraft: (draftId: string, studyId?: string, status?: string, runId?: string | null) => void
}

/** `null` while the connection check is still loading: the table shows its loading state, not a connection notice. */
function resolveConnectionState(
  isTestFeedConnected: boolean,
  readiness: TestFeedConnectionStatus | null,
  isLoadingReadiness: boolean,
): StudySetupsConnectionState | null {
  if (isTestFeedConnected) return 'connected'
  if (readiness?.code === 'SHOPIFY_CONNECTION_SELECTION_REQUIRED') return 'needs-connection'
  if (readiness === null) return isLoadingReadiness ? null : 'failed'
  return 'disconnected'
}

export function StudySetupsRoute({
  shopify,
  verifiedScope,
  isTestFeedConnected,
  readiness,
  isLoadingReadiness,
  onCreateTest,
  onResumeDraft,
}: StudySetupsRouteProps) {
  const authoringList = useStudyAuthoringList(shopify, verifiedScope)
  const bulkDeletion = useBulkStudyDeletion({
    shopify,
    scope: verifiedScope,
    drafts: authoringList.data ?? [],
  })
  const studies = authoringList.data ?? []
  const createTest = () => trackShopifyAction(shopify, 'create-test', onCreateTest)
  const openTest: typeof onResumeDraft = (...args) =>
    trackShopifyAction(shopify, 'open-test', () => onResumeDraft(...args))
  const retry = () => {
    void trackShopifyRefetch(shopify, () => authoringList.refetch())
  }
  const connectionState = resolveConnectionState(isTestFeedConnected, readiness, isLoadingReadiness)
  const isConnected = connectionState === 'connected'
  const records = buildTestsIndexRecords({
    drafts: studies,
    canAuthor: isConnected,
  })
  const runningRecords = isConnected ? records.active : []
  return (
    <s-page heading={pageTitleForView('studies')} inlineSize="large">
      {isConnected && (
        <s-button slot="primary-action" variant="primary" onClick={createTest}>
          {productLanguage.shopify.components.appShell.createNewStudyAction}
        </s-button>
      )}
      <s-stack direction="block" gap="base">
        <s-paragraph color="subdued">{introForView('studies').body}</s-paragraph>
        <RunningTestsSection
          records={runningRecords}
          onOpen={(record) => {
            if (record.action.kind !== 'open-study') throw new Error(`Test ${record.key} has no study to open`)
            openTest(record.action.studyId, record.action.studyId, record.action.status, record.runId)
          }}
          onRetryStatus={retry}
        />
        <StudySetupsPage
          isLoading={connectionState === null}
          savedDrafts={studies}
          isLoadingSavedDrafts={authoringList.isLoading}
          savedDraftsError={authoringList.error}
          onRetrySavedDrafts={retry}
          onResumeDraft={openTest}
          selection={{
            selectedIds: bulkDeletion.selectedIds,
            onChange: bulkDeletion.setSelectedIds,
            onDelete: bulkDeletion.begin,
          }}
          onCreateStudy={createTest}
          state={connectionState ?? 'connected'}
          settingsUrl={buildSettingsViewUrl()}
          isAuthoringAvailable={isConnected}
        />
      </s-stack>
      <BulkStudyDeleteDialog
        open={bulkDeletion.open}
        phase={bulkDeletion.phase}
        confirmationAction={bulkDeletion.confirmationAction}
        actionCount={bulkDeletion.actionCount}
        canConfirm={bulkDeletion.canConfirm}
        items={bulkDeletion.items}
        isDeleting={bulkDeletion.isDeleting}
        message={bulkDeletion.errorMessage}
        onClose={bulkDeletion.close}
        onConfirm={bulkDeletion.confirm}
        onCheckPending={bulkDeletion.checkPending}
        onRetryFailed={bulkDeletion.retryFailed}
      />
    </s-page>
  )
}
